// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchTeacherSubjectsWithStudents } from '../../../services/api';
import {
  fetchAssignments,
  duplicateAssignment,
  deleteAssignment,
  calculateGradingStats,
  ASSIGNMENT_TYPES,
  ASSIGNMENT_STATUSES,
} from '../../../services/assignments';
import './Assignments.css';

export default function AssignmentsHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const teacherId = user?.user_id || user?.id || null;

  const [assignments, setAssignments] = useState<any>([]);
  const [activeClasses, setActiveClasses] = useState<any>([]);
  const [activeSubjects, setActiveSubjects] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  // Filters - pre-populate from navigation state if provided
  const [search, setSearch] = useState<any>('');
  const [selectedClass, setSelectedClass] = useState(location.state?.selectedClassId || 'all');
  const [selectedSubject, setSelectedSubject] = useState(location.state?.selectedSubject || 'all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch teacher's active classes and subjects
      let teacherClasses = [];
      let teacherSubjects = [];
      try {
        const subData = await fetchTeacherSubjectsWithStudents();
        const subjectsList = Array.isArray(subData) ? subData : subData?.subjects || [];
        subjectsList.forEach((sub) => {
          const subName = sub.subject_name || sub.subject || 'Subject';
          if (!teacherSubjects.includes(subName)) {
            teacherSubjects.push(subName);
          }
          (sub.classes || []).forEach((c) => {
            teacherClasses.push({
              class_id: String(c.class_id || c.id),
              class_name: c.class_name || c.name || `${subName} - Class ${c.class_id}`,
              grade_level: c.grade_level ? (String(c.grade_level).toLowerCase().startsWith('grade') ? c.grade_level : `Grade ${c.grade_level}`) : 'Grade 6',
              subject_name: subName,
              students: (c.students || []).map((s) => ({
                student_id: s.student_id || s.id,
                full_name: s.full_name || s.name || `${s.last_name || ''}, ${s.first_name || ''}`.trim() || 'Student',
                student_number: s.student_number || `STU${s.student_id}`,
              })),
            });
          });
        });
        setActiveClasses(teacherClasses);
        setActiveSubjects(teacherSubjects);
      } catch (err: any) {
        console.warn('Could not load teacher active classes:', err);
      }

      const data = await fetchAssignments({
        teacherId,
        teacherClasses,
      });
      setAssignments(data);
    } catch (err: any) {
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  // Compute unique filter options combining active classes & existing assignment classes
  const classOptions = useMemo(() => {
    const map = new Map();
    // Add active classes first
    activeClasses.forEach((c) => {
      map.set(String(c.class_id), { id: String(c.class_id), name: `${c.class_name} (Active Class)` });
    });
    // Add any classes from existing assignments
    assignments.forEach((a) => {
      if (a.classId && !map.has(String(a.classId))) {
        map.set(String(a.classId), { id: String(a.classId), name: a.className || `Class ${a.classId}` });
      }
    });
    return Array.from(map.values());
  }, [activeClasses, assignments]);

  const subjectOptions = useMemo(() => {
    const set = new Set(activeSubjects);
    assignments.forEach((a) => {
      if (a.subject) set.add(a.subject);
    });
    return Array.from(set);
  }, [activeSubjects, assignments]);

  const activeClassIdSet = useMemo(() => {
    return new Set(activeClasses.map((c) => String(c.class_id)));
  }, [activeClasses]);

  // Overall Hub Stats
  const hubStats = useMemo(() => {
    const total = assignments.length;
    let inProgress = 0;
    let needsGrading = 0;
    let completed = 0;

    assignments.forEach((a) => {
      const stats = calculateGradingStats(a);
      if (a.status === 'completed') {
        completed += 1;
      } else if (a.status === 'in_progress' || (a.status === 'assigned' && stats.percentComplete > 0)) {
        inProgress += 1;
      }
      if (stats.percentComplete < 100 && a.status !== 'draft') {
        needsGrading += 1;
      }
    });

    return { total, inProgress, needsGrading, completed };
  }, [assignments]);

  // Filtered assignments
  const filteredAssignments = useMemo(() => {
    return assignments.filter((a) => {
      if (selectedStatus !== 'all' && a.status !== selectedStatus) return false;
      if (selectedClass !== 'all' && a.classId !== selectedClass) return false;
      if (selectedSubject !== 'all' && a.subject !== selectedSubject) return false;
      if (selectedType !== 'all' && a.type !== selectedType) return false;

      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchesTitle = (a.title || '').toLowerCase().includes(q);
        const matchesClass = (a.className || '').toLowerCase().includes(q);
        const matchesStandard = (a.standards || []).some(
          (s) =>
            (s.mlsCode || '').toLowerCase().includes(q) ||
            (s.friendlyTitle || '').toLowerCase().includes(q)
        );
        if (!matchesTitle && !matchesClass && !matchesStandard) return false;
      }

      return true;
    });
  }, [assignments, selectedStatus, selectedClass, selectedSubject, selectedType, search]);

  const handleDuplicate = async (id, e) => {
    e.stopPropagation();
    try {
      await duplicateAssignment(id);
      await loadData();
    } catch (err: any) {
      alert(`Could not duplicate assignment: ${err.message}`);
    }
  };

  const handleDelete = async (id, title, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteAssignment(id);
      await loadData();
    } catch (err: any) {
      alert(`Could not delete assignment: ${err.message}`);
    }
  };

  return (
    <div className="lms-assignments-container">
      {/* Header & Primary Action */}
      <div className="lms-header-row">
        <div>
          <h1 className="lms-header-title">Learning Tasks</h1>
          <p className="lms-header-subtitle">
            Create standards-aligned assessments, manage class evaluations, and grade student work with interactive scoring grids.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            className="lms-btn-primary"
            onClick={() => navigate('/teacher/assignments/new')}
          >
            <span>+</span> Create New Task
          </button>
        </div>
      </div>

      {/* Teacher Grading Suite Quick-Access Navigation Bar */}
      <div
        className="lms-grading-suite-bar"
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.25rem' }}>🎯</span>
          <div>
            <strong style={{ fontSize: '0.95rem', color: '#1e293b' }}>Teacher Grading Suite</strong>
            <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: '8px' }}>
              Direct access to evaluations, progression scoring, and reports for active classes
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="lms-btn-secondary lms-btn-sm"
            onClick={() => navigate('/teacher/standards-progression')}
            title="Open Data Entry Progression Grid"
            style={{ fontSize: '0.82rem', padding: '6px 12px' }}
          >
            📝 Data Entry
          </button>
          <button
            type="button"
            className="lms-btn-secondary lms-btn-sm"
            onClick={() => navigate('/teacher/student-profiles')}
            title="Open Student Profiles"
            style={{ fontSize: '0.82rem', padding: '6px 12px' }}
          >
            👥 Student Profiles
          </button>
          <button
            type="button"
            className="lms-btn-secondary lms-btn-sm"
            onClick={() => navigate('/teacher/data-points-report')}
            title="Open View Data Reports"
            style={{ fontSize: '0.82rem', padding: '6px 12px' }}
          >
            📊 View Data
          </button>
          <button
            type="button"
            className="lms-btn-secondary lms-btn-sm"
            onClick={() => navigate('/teacher/standard-progressions')}
            title="Open Standard Progressions Reference"
            style={{ fontSize: '0.82rem', padding: '6px 12px' }}
          >
            📈 Progressions
          </button>
          <button
            type="button"
            className="lms-btn-secondary lms-btn-sm"
            onClick={() => navigate('/teacher/classes')}
            title="Manage Teacher Classes & Rosters"
            style={{ fontSize: '0.82rem', padding: '6px 12px' }}
          >
            🏫 Classes
          </button>
        </div>
      </div>

      {/* High-level Overview Cards */}
      <div className="lms-stats-grid">
        <div className="lms-stat-card">
          <span className="lms-stat-label">Total Assessments</span>
          <span className="lms-stat-value">{hubStats.total}</span>
          <span className="lms-stat-hint">Across all active classes</span>
        </div>
        <div className="lms-stat-card">
          <span className="lms-stat-label">Active / In Progress</span>
          <span className="lms-stat-value" style={{ color: '#d97706' }}>
            {hubStats.inProgress}
          </span>
          <span className="lms-stat-hint">Currently receiving grades</span>
        </div>
        <div className="lms-stat-card">
          <span className="lms-stat-label">Needs Grading</span>
          <span className="lms-stat-value" style={{ color: '#2563eb' }}>
            {hubStats.needsGrading}
          </span>
          <span className="lms-stat-hint">Unfinished student-standard cells</span>
        </div>
        <div className="lms-stat-card">
          <span className="lms-stat-label">Completed & Graded</span>
          <span className="lms-stat-value" style={{ color: '#059669' }}>
            {hubStats.completed}
          </span>
          <span className="lms-stat-hint">Finalized records</span>
        </div>
      </div>

      {/* Toolbar & Filter Controls */}
      <div className="lms-toolbar">
        <div className="lms-toolbar-row">
          <div className="lms-search-input-wrapper">
            <span className="lms-search-icon" aria-hidden="true">🔍</span>
            <input
              type="text"
              className="lms-search-input"
              placeholder="Search by title, class, or standard (e.g. 6.RL.1.A)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search assignments"
            />
          </div>

          <div className="lms-filter-group">
            <select
              className="lms-select"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              aria-label="Filter by class"
            >
              <option value="all">All Classes</option>
              {classOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              className="lms-select"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              aria-label="Filter by subject"
            >
              <option value="all">All Subjects</option>
              {subjectOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              className="lms-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              aria-label="Filter by type"
            >
              <option value="all">All Types</option>
              {ASSIGNMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <div className="lms-view-toggle">
              <button
                type="button"
                className={`lms-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                aria-label="Card view"
              >
                Cards
              </button>
              <button
                type="button"
                className={`lms-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                aria-label="Table view"
              >
                Table
              </button>
            </div>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="lms-status-tabs">
          <button
            type="button"
            className={`lms-status-tab ${selectedStatus === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('all')}
          >
            All ({assignments.length})
          </button>
          {ASSIGNMENT_STATUSES.map((st) => {
            const count = assignments.filter((a) => a.status === st.value).length;
            return (
              <button
                key={st.value}
                type="button"
                className={`lms-status-tab ${selectedStatus === st.value ? 'active' : ''}`}
                onClick={() => setSelectedStatus(st.value)}
              >
                {st.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#6b7280' }}>
          <p>Loading assignments and scoring records...</p>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div style={{
          background: '#ffffff',
          border: '1px dashed #cbd5e1',
          borderRadius: '12px',
          padding: '48px 24px',
          textAlign: 'center',
        }}>
          <h3 style={{ margin: '0 0 8px', color: '#1e293b' }}>No assignments found</h3>
          <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.9rem' }}>
            {search || selectedStatus !== 'all' || selectedClass !== 'all' || selectedSubject !== 'all'
              ? 'Try adjusting your search criteria or filter selections.'
              : 'Create your first standards-based assignment to start evaluating students with spreadsheet grading grids.'}
          </p>
          <button
            type="button"
            className="lms-btn-primary"
            onClick={() => navigate('/teacher/assignments/new')}
          >
            Create New Assignment
          </button>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="lms-cards-grid">
          {filteredAssignments.map((a) => {
            const stats = calculateGradingStats(a);
            const statusConfig = ASSIGNMENT_STATUSES.find((s) => s.value === a.status) || ASSIGNMENT_STATUSES[0];

            return (
              <div key={a.id} className="lms-card">
                <div className="lms-card-header">
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="lms-badge lms-badge-type">{a.type || 'Assignment'}</span>
                    {activeClassIdSet.has(String(a.classId)) && (
                      <span
                        className="lms-badge"
                        style={{
                          backgroundColor: '#ecfdf5',
                          color: '#047857',
                          border: '1px solid #a7f3d0',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                        }}
                      >
                        ✓ Active Class
                      </span>
                    )}
                  </div>
                  <span className={`lms-badge lms-badge-${a.status}`}>
                    {statusConfig.label}
                  </span>
                </div>

                <h2 className="lms-card-title">{a.title}</h2>
                <div className="lms-card-meta">
                  <span>{a.className}</span>
                  <span>•</span>
                  <span>{a.subject}</span>
                  {a.dueDate && (
                    <>
                      <span>•</span>
                      <span>Due {a.dueDate}</span>
                    </>
                  )}
                </div>

                {/* Standards Assessed */}
                <div className="lms-standards-pills">
                  {(a.standards || []).map((std) => (
                    <span
                      key={std.mlsCode}
                      className="lms-standard-pill"
                      title={`${std.mlsCode}: ${std.description || std.friendlyTitle}`}
                    >
                      {std.mlsCode}
                    </span>
                  ))}
                </div>

                {/* Grading Completion Bar */}
                <div className="lms-progress-wrapper">
                  <div className="lms-progress-labels">
                    <span>Grading Progress</span>
                    <span>
                      {stats.gradedCells} / {stats.totalCells} cells ({stats.percentComplete}%)
                    </span>
                  </div>
                  <div className="lms-progress-track">
                    <div
                      className="lms-progress-fill"
                      style={{
                        width: `${stats.percentComplete}%`,
                        backgroundColor:
                          stats.percentComplete === 100
                            ? '#059669'
                            : stats.percentComplete > 50
                            ? '#2563eb'
                            : '#d97706',
                      }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="lms-card-actions">
                  <button type="button" className="lms-btn-secondary lms-btn-sm"
                    onClick={() => navigate(`/teacher/assignments/${a.id}/edit`)}>
                    Manage Task
                  </button>
                  <button
                    type="button"
                    className="lms-btn-primary lms-btn-sm"
                    onClick={() => navigate(`/teacher/assignments/${a.id}`)}
                  >
                    📊 Grade Work
                  </button>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    {a.status !== 'draft' && (
                      <button
                        type="button"
                        className="lms-btn-secondary lms-btn-sm"
                        onClick={() => navigate(`/teacher/data-points-report?assignment=${encodeURIComponent(a.id)}`)}
                        title="See this assignment's graded data points in View Data"
                      >
                        📈 View Data
                      </button>
                    )}
                    <button
                      type="button"
                      className="lms-btn-secondary lms-btn-sm"
                      onClick={() => navigate(`/teacher/assignments/${a.id}/print`)}
                      title="Print scoring grid"
                    >
                      🖨️ Print
                    </button>
                    <button
                      type="button"
                      className="lms-btn-secondary lms-btn-sm"
                      onClick={(e) => handleDuplicate(a.id, e)}
                      title="Duplicate assignment"
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className="lms-btn-secondary lms-btn-sm"
                      style={{ color: '#dc2626' }}
                      onClick={(e) => handleDelete(a.id, a.title, e)}
                      title="Delete assignment"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="lms-table-container">
          <table className="lms-table">
            <thead>
              <tr>
                <th>Title & Type</th>
                <th>Class</th>
                <th>Subject</th>
                <th>Assessed Standards</th>
                <th>Due Date</th>
                <th>Students</th>
                <th>Grading Progress</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map((a) => {
                const stats = calculateGradingStats(a);
                const statusConfig = ASSIGNMENT_STATUSES.find((s) => s.value === a.status) || ASSIGNMENT_STATUSES[0];

                return (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{a.title}</div>
                      <span className="lms-badge lms-badge-type" style={{ marginTop: '4px' }}>
                        {a.type}
                      </span>
                    </td>
                    <td>
                      <div>{a.className}</div>
                      {activeClassIdSet.has(String(a.classId)) && (
                        <span
                          className="lms-badge"
                          style={{
                            backgroundColor: '#ecfdf5',
                            color: '#047857',
                            border: '1px solid #a7f3d0',
                            fontSize: '0.68rem',
                            padding: '1px 6px',
                            marginTop: '3px',
                            display: 'inline-block',
                          }}
                        >
                          Active
                        </span>
                      )}
                    </td>
                    <td>{a.subject}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '240px' }}>
                        {(a.standards || []).map((std) => (
                          <span
                            key={std.mlsCode}
                            className="lms-standard-pill"
                            title={std.description || std.friendlyTitle}
                          >
                            {std.mlsCode}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{a.dueDate || '—'}</td>
                    <td>{(a.students || []).length}</td>
                    <td style={{ minWidth: '140px' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>
                        {stats.percentComplete}% ({stats.gradedCells}/{stats.totalCells})
                      </div>
                      <div className="lms-progress-track">
                        <div
                          className="lms-progress-fill"
                          style={{ width: `${stats.percentComplete}%` }}
                        />
                      </div>
                    </td>
                    <td>
                      <span className={`lms-badge lms-badge-${a.status}`}>
                        {statusConfig.label}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" className="lms-btn-secondary lms-btn-sm"
                        onClick={() => navigate(`/teacher/assignments/${a.id}/edit`)}>
                        Manage Task
                      </button>
                      <button
                        type="button"
                        className="lms-btn-primary lms-btn-sm"
                        style={{ marginRight: '6px' }}
                        onClick={() => navigate(`/teacher/assignments/${a.id}`)}
                      >
                        Grade
                      </button>
                      {a.status !== 'draft' && (
                        <button
                          type="button"
                          className="lms-btn-secondary lms-btn-sm"
                          style={{ marginRight: '6px' }}
                          onClick={() => navigate(`/teacher/data-points-report?assignment=${encodeURIComponent(a.id)}`)}
                          title="See this assignment's graded data points in View Data"
                        >
                          View Data
                        </button>
                      )}
                      <button
                        type="button"
                        className="lms-btn-secondary lms-btn-sm"
                        style={{ marginRight: '6px' }}
                        onClick={() => navigate(`/teacher/assignments/${a.id}/print`)}
                      >
                        Print
                      </button>
                      <button
                        type="button"
                        className="lms-btn-secondary lms-btn-sm"
                        onClick={(e) => handleDelete(a.id, a.title, e)}
                        style={{ color: '#dc2626' }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
